/* @section: impact-page */
import { motion } from "framer-motion";
import PageLayout from "@/components/Layout";
import { impactStats, programs, geographicAreas } from "@/data";
import { Users, MapPin, TrendingUp, Award } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

function PageHero() {
  return (
    <section className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(135deg, oklch(0.88 0.12 225), oklch(0.76 0.16 235))" }}>
      <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 60% 40%, oklch(0.99 0.01 50) 0%, transparent 60%)" }} />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold uppercase tracking-widest bg-white/50 px-3 py-1 rounded-full border border-primary/30" style={{ color: "oklch(0.35 0.15 228)" }}>Our Impact</span>
            <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-4 leading-tight" style={{ color: "oklch(0.15 0.08 235)" }}>Measuring Our Impact</h1>
          </motion.div>
          <motion.p variants={fadeUp} className="text-lg leading-relaxed" style={{ color: "oklch(0.25 0.06 230)" }}>
            Since 2018, FLYN has reached tens of thousands of beneficiaries across Southern Shan State through humanitarian aid, community development, and livelihoods support.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function KeyMetrics() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">By The Numbers</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Key Impact Metrics</h2>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {impactStats.map((stat) => (
            <motion.div key={stat.label} variants={fadeUp} className="p-8 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-lg transition-all duration-300 text-center">
              <p className="text-4xl md:text-5xl font-bold font-mono mb-2" style={{ color: "oklch(0.50 0.19 228)" }}>
                {stat.value}<span style={{ color: "var(--accent)" }}>{stat.suffix}</span>
              </p>
              <p className="font-semibold text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ProgramImpact() {
  const highlighted = [
    { title: "Earthquake Emergency Response (2025)", icon: "🚨", stats: [{ label: "Villages Reached", value: "16" }, { label: "Households", value: "1,951" }, { label: "Individuals", value: "8,436" }, { label: "Rice Distributed (pyi)", value: "47,970" }], color: "oklch(0.55 0.22 15)" },
    { title: "VSLA Programme (2022–2024)", icon: "💰", stats: [{ label: "VSLA Groups", value: "18" }, { label: "Members", value: "458" }, { label: "Women Members", value: "350" }, { label: "Villages", value: "18" }], color: "oklch(0.72 0.14 50)" },
    { title: "SCLR Programme (2023–2025)", icon: "🌱", stats: [{ label: "Beneficiaries", value: "6,186" }, { label: "Women Beneficiaries", value: "3,062" }, { label: "Micro-Projects", value: "32" }, { label: "Community Contribution (MMK)", value: "44.1M" }], color: "oklch(0.48 0.12 170)" },
  ];
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Program Highlights</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Impact by Program</h2>
          </motion.div>
        </motion.div>
        <div className="space-y-8">
          {highlighted.map((prog, i) => (
            <motion.div key={prog.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="p-8 rounded-2xl bg-background border border-border hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">{prog.icon}</span>
                <h3 className="font-bold text-lg">{prog.title}</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {prog.stats.map((s) => (
                  <div key={s.label} className="text-center p-4 rounded-xl bg-muted/50 border border-border">
                    <p className="text-2xl font-bold font-mono mb-1" style={{ color: prog.color }}>{s.value}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GeographicImpact() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Coverage</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Geographic Reach</h2>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {geographicAreas.map((area) => (
            <motion.div key={area.name} variants={fadeUp} className="p-7 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-md transition-all duration-300">
              <MapPin className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-bold text-lg mb-1">{area.name}</h3>
              <p className="text-sm text-muted-foreground mb-4" style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }}>{area.nameMM}</p>
              <div className="flex flex-wrap gap-2">
                {area.programs.map((p) => (
                  <span key={p} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium border border-primary/20">{p}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function StrategicGoals() {
  const goals = [
    { icon: <TrendingUp className="w-6 h-6" />, title: "Enhance Community Resilience", desc: "Strengthen SCLR frameworks enabling communities to lead their own recovery and development sustainably." },
    { icon: <Users className="w-6 h-6" />, title: "Women's Leadership", desc: "Increase women's leadership roles to 35% and ensure 75% of CBOs achieve financial and administrative independence." },
    { icon: <Award className="w-6 h-6" />, title: "Professional Excellence", desc: "Becoming a more professional and safety-compliant organization with strong external partnership networks." },
    { icon: <MapPin className="w-6 h-6" />, title: "Expand Geographic Coverage", desc: "Scaling programs to reach 5,800+ beneficiaries in 2026 across expanded geographic areas." },
  ];
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Looking Ahead</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Strategic Goals 2026+</h2>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {goals.map((g) => (
            <motion.div key={g.title} variants={fadeUp} className="flex gap-5 p-6 rounded-2xl bg-background border border-border hover:border-primary/40 hover:shadow-md transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">{g.icon}</div>
              <div>
                <h3 className="font-bold text-base mb-2">{g.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{g.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default function Impact() {
  return (
    <PageLayout>
      <PageHero />
      <KeyMetrics />
      <ProgramImpact />
      <GeographicImpact />
      <StrategicGoals />
    </PageLayout>
  );
}
